import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { Dialog } from './Dialog'
import { Button, type ButtonVariant } from './Button'

export interface ConfirmOptions {
  title?: ReactNode
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** 'danger' renders a red confirm button. */
  tone?: 'default' | 'danger'
  /** Alert-mode: single OK button, resolves true. */
  alertOnly?: boolean
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>
type AlertFn = (options: ConfirmOptions | string) => Promise<void>

interface ConfirmContextValue { confirm: ConfirmFn; alert: AlertFn }

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

interface PendingState extends ConfirmOptions { open: boolean }

/**
 * ConfirmProvider — mount once in the shell. Then anywhere:
 *
 *   const { confirm, alert } = useConfirm()
 *   if (await confirm({ title: 'Deactivate facility?', tone: 'danger' })) { … }
 *   await alert('Export request submitted.')
 *
 * A drop-in replacement for native `window.confirm` / `window.alert`.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PendingState>({ open: false })
  const resolver = useRef<((value: boolean) => void) | null>(null)

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value)
    resolver.current = null
    setState(s => ({ ...s, open: false }))
  }, [])

  const confirm = useCallback<ConfirmFn>((options) => {
    const opts = typeof options === 'string' ? { message: options } : options
    // Settle any previous pending confirm as cancelled.
    resolver.current?.(false)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
      setState({ open: true, ...opts })
    })
  }, [])

  const alert = useCallback<AlertFn>(async (options) => {
    const opts = typeof options === 'string' ? { message: options } : options
    await confirm({ ...opts, alertOnly: true })
  }, [confirm])

  const tone = state.tone ?? 'default'
  const confirmVariant: ButtonVariant = tone === 'danger' ? 'danger' : 'primary'

  return (
    <ConfirmContext.Provider value={{ confirm, alert }}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => settle(false)}
        size="sm"
        role="alertdialog"
        hideClose
        zIndexClassName="z-[90]"
        title={state.title ?? (state.alertOnly ? 'Notice' : 'Please confirm')}
        footer={
          <>
            {!state.alertOnly && (
              <Button variant="secondary" onClick={() => settle(false)}>{state.cancelLabel ?? 'Cancel'}</Button>
            )}
            <Button variant={confirmVariant} onClick={() => settle(true)} autoFocus>
              {state.confirmLabel ?? (state.alertOnly ? 'OK' : 'Confirm')}
            </Button>
          </>
        }
      >
        <div className="flex gap-3">
          <span className={tone === 'danger' ? 'text-status-danger' : 'text-status-info'}>
            {tone === 'danger' ? <AlertTriangle className="w-5 h-5" aria-hidden /> : <Info className="w-5 h-5" aria-hidden />}
          </span>
          <div className="text-sm text-fg whitespace-pre-line">{state.message}</div>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    // Fallback keeps components usable outside the shell (tests, storybook).
    return {
      confirm: async (o) => window.confirm(typeof o === 'string' ? o : String(o.message ?? o.title ?? '')),
      alert: async (o) => { window.alert(typeof o === 'string' ? o : String(o.message ?? o.title ?? '')) },
    }
  }
  return ctx
}
